/**
 * Resultado de la proposal AGL del alta native (aceptar la nota legal, /payment/4).
 *
 * El gateway devuelve TAL CUAL la respuesta de AGL, y AGL contesta HTTP 200 también
 * cuando rechaza: `{"status":"FAILURE","messages":[{"type":"ERROR","description":
 * "Invalid fiscal codeDBLSMN96A08L219Q "}]}`. ms-agl solo persiste la proposal y
 * nace la suscripción con `SUCCESS`, así que seguir a ensure-funds tras un FAILURE
 * acababa en 404 «sin suscripción native» y el cliente no veía nada (bobolo,
 * 30-oct-2026). Aquí se decide si se sigue o se le pide corregir sus datos.
 */
export type ProposalFailureKindT = "FISCAL_CODE" | "GENERIC";

export type ProposalOutcomeT =
  | { ok: true }
  | { ok: false; kind: ProposalFailureKindT };

const INVALID_FISCAL_CODE = /invalid\s+fiscal\s+code/i;

const descriptions = (response: unknown): string[] => {
  const messages = (response as { messages?: unknown } | null)?.messages;
  if (!Array.isArray(messages)) return [];
  return messages
    .map((m) => (m as { description?: unknown } | null)?.description)
    .filter((d): d is string => "string" === typeof d);
};

export const proposalOutcome = (response: unknown): ProposalOutcomeT => {
  const status = (response as { status?: unknown } | null)?.status;
  if ("SUCCESS" === status) return { ok: true };
  const kind = descriptions(response).some((d) => INVALID_FISCAL_CODE.test(d))
    ? "FISCAL_CODE"
    : "GENERIC";
  return { ok: false, kind };
};

/**
 * Qué datos anagráficos NO cuadran con los 11 primeros caracteres del codice
 * fiscale (cognome, nome, año, mes, día y sexo). El comune (posiciones 12-15) no
 * se comprueba: el paso de nacimiento no guarda el código catastal. Sirve para
 * orientar al cliente, nunca para bloquearle: ante la duda (CF incompleto, datos
 * vacíos) no señala nada.
 */
export type AnagraphicFieldT = "lastName" | "firstName" | "birthDate" | "gender";

export interface AnagraphicDataI {
  fiscalCode?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** YYYY-MM-DD (puede traer hora detrás). */
  birthDate?: string | null;
  /** «Male»/«Female», «M»/«F»… solo cuenta la inicial. */
  gender?: string | null;
}

const MONTHS = "ABCDEHLMPRST";
// Omocodia: en las posiciones numéricas la cifra puede venir sustituida por letra.
const OMOCODE = "LMNPQRSTUV";
const NUMERIC_POSITIONS = [6, 7, 9, 10];

const lettersOf = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const isVowel = (c: string) => "AEIOU".includes(c);

const consonants = (value: string) => value.split("").filter((c) => !isVowel(c));
const vowels = (value: string) => value.split("").filter(isVowel);

const pad = (code: string): string => (code + "XXX").slice(0, 3);

export const surnameCode = (lastName: string): string => {
  const l = lettersOf(lastName);
  return pad([...consonants(l), ...vowels(l)].join(""));
};

export const nameCode = (firstName: string): string => {
  const l = lettersOf(firstName);
  const c = consonants(l);
  if (c.length >= 4) return c[0] + c[2] + c[3];
  return pad([...c, ...vowels(l)].join(""));
};

const decodeOmocode = (cf: string): string =>
  cf
    .split("")
    .map((c, i) =>
      NUMERIC_POSITIONS.includes(i) && OMOCODE.includes(c)
        ? String(OMOCODE.indexOf(c))
        : c,
    )
    .join("");

export const codiceFiscaleMismatches = (
  data: AnagraphicDataI,
): AnagraphicFieldT[] => {
  const raw = (data.fiscalCode ?? "").toUpperCase().replace(/\s/g, "");
  if (16 !== raw.length) return [];
  const cf = decodeOmocode(raw);
  const out: AnagraphicFieldT[] = [];

  if (data.lastName && lettersOf(data.lastName) && surnameCode(data.lastName) !== cf.slice(0, 3)) {
    out.push("lastName");
  }
  if (data.firstName && lettersOf(data.firstName) && nameCode(data.firstName) !== cf.slice(3, 6)) {
    out.push("firstName");
  }

  const birth = /^(\d{4})-(\d{2})-(\d{2})/.exec(data.birthDate ?? "");
  const day = Number(cf.slice(9, 11));
  if (birth && !Number.isNaN(day)) {
    const [, year, month, dd] = birth;
    const sameDate =
      year.slice(2) === cf.slice(6, 8) &&
      MONTHS[Number(month) - 1] === cf[8] &&
      Number(dd) === day % 40;
    if (!sameDate) out.push("birthDate");
  }

  const g = (data.gender ?? "").trim().toUpperCase()[0];
  if (("M" === g || "F" === g) && !Number.isNaN(day)) {
    const cfGender = day > 40 ? "F" : "M";
    if (g !== cfGender) out.push("gender");
  }

  return out;
};
